import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import {
  Trophy,
  Users,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Package,
  Star,
  Award,
  Medal,
  Crown,
  User,
  Truck
} from 'lucide-react'

interface Competition {
  id: string
  title: string
  description: string
  start_date: string
  end_date: string
  status: 'upcoming' | 'active' | 'completed' | 'cancelled'
  prize: string
  created_at: string
  created_by: string
  participants: {
    manager_id: string
    manager_name: string
    team_name: string
    team_color: string
    score: number
    completed_missions: number
    team_members: number
  }[]
  winner_id?: string
  my_manager?: {
    id: string
    full_name: string
  }
}

export function DeliveryCompetitionsPage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [competitions, setCompetitions] = useState<Competition[]>([])
  const [loading, setLoading] = useState(true)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [teamMembers, setTeamMembers] = useState<any[]>([])
  const [selectedCompetition, setSelectedCompetition] = useState<Competition | null>(null)
  const [showTeamModal, setShowTeamModal] = useState(false)

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }

    fetchCompetitions()
  }, [user, navigate])

  const fetchCompetitions = async () => {
    if (!user) return

    try {
      setLoading(true)
      
      // First, get the manager ID for this delivery partner
      const { data: deliveryPartner, error: deliveryError } = await supabase
        .from('delivery_partners')
        .select('manager_id')
        .eq('id', user.id)
        .single()

      if (deliveryError) {
        // If no manager found, show empty state
        setCompetitions([])
        setLoading(false)
        return
      }

      const managerId = deliveryPartner.manager_id
      
      if (!managerId) {
        // If no manager assigned, show empty state
        setCompetitions([])
        setLoading(false)
        return
      }

      // Get manager details
      const { data: managerData, error: managerError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('id', managerId)
        .single()

      if (managerError) throw managerError

      // Get all competitions where this manager is a participant
      const { data: participations, error: participationsError } = await supabase
        .from('competition_participants')
        .select('competition_id')
        .eq('manager_id', managerId)

      if (participationsError) throw participationsError

      if (!participations || participations.length === 0) {
        setCompetitions([])
        setLoading(false)
        return
      }

      const competitionIds = participations.map(p => p.competition_id)

      // Get competition details
      const { data, error } = await supabase
        .from('competitions')
        .select('*')
        .in('id', competitionIds)
        .order('created_at', { ascending: false })

      if (error) throw error

      // For each competition, fetch participants
      const competitionsWithParticipants = await Promise.all(
        (data || []).map(async (competition) => {
          const { data: participants, error: participantsError } = await supabase
            .from('competition_participants')
            .select(`
              manager_id,
              team_name,
              team_color,
              score,
              completed_missions,
              profiles:manager_id (
                full_name
              )
            `)
            .eq('competition_id', competition.id)

          if (participantsError) throw participantsError

          // For each participant, count team members
          const participantsWithTeamSize = await Promise.all(
            (participants || []).map(async (participant) => {
              const { count, error: countError } = await supabase
                .from('delivery_partners')
                .select('*', { count: 'exact' })
                .eq('manager_id', participant.manager_id)
                .eq('status', 'active')

              if (countError) throw countError

              return {
                manager_id: participant.manager_id,
                manager_name: participant.profiles?.full_name || 'Sans nom',
                team_name: participant.team_name,
                team_color: participant.team_color,
                score: participant.score,
                completed_missions: participant.completed_missions,
                team_members: count || 0
              }
            })
          )

          return {
            ...competition,
            participants: participantsWithTeamSize,
            my_manager: managerData
          }
        })
      )

      setCompetitions(competitionsWithParticipants)
    } catch (err) {
      console.error('Error fetching competitions:', err)
      setToastMessage('Erreur lors du chargement des compétitions')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const fetchTeamMembers = async (managerId: string) => {
    try {
      const { data, error } = await supabase
        .from('delivery_partners')
        .select(`
          id,
          applicant_name,
          applicant_email,
          vehicle_type,
          delivery_zone,
          rating,
          total_deliveries
        `)
        .eq('manager_id', managerId)
        .eq('status', 'active')
        .order('rating', { ascending: false })

      if (error) throw error
      setTeamMembers(data || [])
    } catch (err) {
      console.error('Error fetching team members:', err)
      setToastMessage('Erreur lors du chargement des membres de l\'équipe')
      setToastType('error')
      setShowToast(true)
    }
  }

  const getStatusBadge = (status: Competition['status']) => {
    switch (status) {
      case 'upcoming':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Clock className="h-3 w-3 mr-1" />
            À venir
          </span>
        )
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            En cours
          </span>
        )
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            <Trophy className="h-3 w-3 mr-1" />
            Terminée
          </span>
        )
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="h-3 w-3 mr-1" />
            Annulée
          </span>
        )
      default:
        return null
    }
  }

  const getTeamInfo = (competition: Competition) => {
    if (!competition.my_manager) return null
    return competition.participants.find(p => p.manager_id === competition.my_manager?.id) || null
  }

  const getTeamRank = (competition: Competition) => {
    if (!competition.my_manager) return null
    const sortedParticipants = [...competition.participants].sort((a, b) => b.score - a.score)
    const teamIndex = sortedParticipants.findIndex(p => p.manager_id === competition.my_manager?.id)
    return teamIndex !== -1 ? teamIndex + 1 : null
  }

  const getTeamPosition = (rank: number) => {
    if (rank === 1) return { text: '1er', color: 'text-amber-500', icon: <Crown className="h-5 w-5 text-amber-500" /> }
    if (rank === 2) return { text: '2ème', color: 'text-gray-500', icon: <Medal className="h-5 w-5 text-gray-500" /> }
    if (rank === 3) return { text: '3ème', color: 'text-amber-700', icon: <Medal className="h-5 w-5 text-amber-700" /> }
    return { text: `${rank}ème`, color: 'text-gray-700', icon: null }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      {/* Team Members Modal */}
      {showTeamModal && selectedCompetition && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Mon Équipe
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Membres de votre équipe pour la compétition "{selectedCompetition.title}"
                </p>
              </div>
              <button
                onClick={() => setShowTeamModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Team Info */}
              {(() => {
                const teamInfo = getTeamInfo(selectedCompetition)
                if (!teamInfo) return null
                
                return (
                  <div className="p-4 rounded-lg" style={{ backgroundColor: `${teamInfo.team_color}20` }}>
                    <div className="flex items-center">
                      <div 
                        className="h-10 w-10 rounded-full flex items-center justify-center text-white"
                        style={{ backgroundColor: teamInfo.team_color }}
                      >
                        <Trophy className="h-5 w-5" />
                      </div>
                      <div className="ml-3">
                        <h4 className="text-lg font-medium">{teamInfo.team_name}</h4>
                        <div className="flex items-center text-sm text-gray-500">
                          <Users className="h-4 w-4 mr-1" />
                          {teamInfo.team_members} membres
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-4">
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <div className="text-xs text-gray-500">Score</div>
                        <div className="text-xl font-bold">{teamInfo.score}</div>
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <div className="text-xs text-gray-500">Missions</div>
                        <div className="text-xl font-bold">{teamInfo.completed_missions}</div>
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <div className="text-xs text-gray-500">Position</div>
                        <div className="text-xl font-bold">{getTeamRank(selectedCompetition)}</div>
                      </div>
                    </div>
                  </div>
                )
              })()}

              {/* Team Members */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-4">Membres de l'équipe</h4>
                {teamMembers.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      Aucun membre
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Votre équipe n'a pas encore de livreurs assignés
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {teamMembers.map(member => (
                      <div key={member.id} className="bg-white p-4 border border-gray-200 rounded-lg shadow-sm">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <User className="h-5 w-5 text-gray-500" />
                            </div>
                            <div className="ml-3">
                              <h5 className="text-sm font-medium text-gray-900">{member.applicant_name}</h5>
                              <p className="text-xs text-gray-500">{member.applicant_email}</p>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <div className="flex items-center mr-4">
                              <Star className="h-4 w-4 text-yellow-400 mr-1" />
                              <span className="text-sm font-medium">{member.rating?.toFixed(1) || '0.0'}</span>
                            </div>
                            <div className="flex items-center">
                              <Package className="h-4 w-4 text-gray-400 mr-1" />
                              <span className="text-sm">{member.total_deliveries || 0}</span>
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center text-xs text-gray-500">
                          <Truck className="h-3 w-3 mr-1" />
                          <span className="capitalize mr-3">
                            {member.vehicle_type === 'bike' ? 'Vélo' :
                             member.vehicle_type === 'motorcycle' ? 'Moto' :
                             member.vehicle_type === 'car' ? 'Voiture' :
                             member.vehicle_type === 'van' ? 'Camionnette' : 'À pied'}
                          </span>
                          <MapPin className="h-3 w-3 mr-1" />
                          <span>{member.delivery_zone}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button
                variant="secondary"
                onClick={() => setShowTeamModal(false)}
              >
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Top Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Trophy className="h-8 w-8 text-primary" />
                <span className="ml-2 text-xl font-bold text-gray-900">
                  Compétitions
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {competitions.length === 0 ? (
          <div className="bg-white shadow rounded-lg">
            <div className="text-center py-12">
              <Trophy className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune compétition</h3>
              <p className="mt-1 text-sm text-gray-500">
                Votre équipe n'est inscrite à aucune compétition pour le moment
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Active Competitions */}
            {competitions.filter(comp => comp.status === 'active').length > 0 && (
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">
                    Compétitions en cours
                  </h2>
                </div>
                <div className="p-6">
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {competitions
                      .filter(comp => comp.status === 'active')
                      .map(competition => {
                        const teamInfo = getTeamInfo(competition)
                        const teamRank = getTeamRank(competition)
                        const position = teamRank ? getTeamPosition(teamRank) : null
                        
                        return (
                          <div 
                            key={competition.id}
                            className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                          >
                            <div 
                              className="p-5 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-purple-600"
                              style={teamInfo ? {
                                background: `linear-gradient(to right, ${teamInfo.team_color}, ${adjustColor(teamInfo.team_color, -20)})`
                              } : {}}
                            >
                              <div className="flex justify-between items-start">
                                <Trophy className="h-8 w-8 text-white" />
                                {getStatusBadge(competition.status)}
                              </div>
                              <h3 className="mt-2 text-xl font-bold text-white">{competition.title}</h3>
                              <div className="mt-2 flex items-center text-white/80">
                                <Calendar className="h-4 w-4 mr-1" />
                                <span className="text-sm">
                                  {new Date(competition.start_date).toLocaleDateString()} - {new Date(competition.end_date).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            
                            <div className="p-5">
                              {teamInfo && (
                                <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: `${teamInfo.team_color}20` }}>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                      <div 
                                        className="h-8 w-8 rounded-full flex items-center justify-center text-white"
                                        style={{ backgroundColor: teamInfo.team_color }}
                                      >
                                        <Users className="h-4 w-4" />
                                      </div>
                                      <div className="ml-2">
                                        <h4 className="text-sm font-medium">{teamInfo.team_name}</h4>
                                        <div className="flex items-center text-xs text-gray-500">
                                          <Package className="h-3 w-3 mr-1" />
                                          {teamInfo.completed_missions} missions
                                        </div>
                                      </div>
                                    </div>
                                    {position && position.icon && (
                                      <div className="flex flex-col items-center">
                                        {position.icon}
                                        <span className={`text-xs font-bold ${position.color}`}>{position.text}</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="mt-3 flex items-center justify-between">
                                    <div className="text-sm font-bold">{teamInfo.score} points</div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedCompetition(competition)
                                        fetchTeamMembers(competition.my_manager!.id)
                                        setShowTeamModal(true)
                                      }}
                                    >
                                      Voir l'équipe
                                    </Button>
                                  </div>
                                </div>
                              )}
                              
                              {competition.prize && (
                                <div className="flex items-center mb-4 text-amber-600">
                                  <Award className="h-5 w-5 mr-2" />
                                  <span className="text-sm font-medium">{competition.prize}</span>
                                </div>
                              )}
                              
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Classement</h4>
                              <div className="space-y-2 mb-4">
                                {[...competition.participants]
                                  .sort((a, b) => b.score - a.score)
                                  .slice(0, 5) // Show top 5
                                  .map((participant, index) => (
                                    <div 
                                      key={participant.manager_id}
                                      className={`flex items-center justify-between p-2 rounded-lg ${
                                        participant.manager_id === competition.my_manager?.id ? 'border-2' : ''
                                      }`}
                                      style={{ 
                                        backgroundColor: `${participant.team_color}20`,
                                        borderColor: participant.manager_id === competition.my_manager?.id ? participant.team_color : 'transparent'
                                      }}
                                    >
                                      <div className="flex items-center">
                                        <div 
                                          className="h-6 w-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                          style={{ backgroundColor: participant.team_color }}
                                        >
                                          {index + 1}
                                        </div>
                                        <span className="ml-2 text-sm font-medium">{participant.team_name}</span>
                                      </div>
                                      <div className="text-sm font-bold">{participant.score} pts</div>
                                    </div>
                                  ))
                                }
                              </div>
                              
                              <div className="text-xs text-gray-500">
                                {competition.participants.length} équipe(s) en compétition
                              </div>
                            </div>
                          </div>
                        )
                      })}
                  </div>
                </div>
              </div>
            )}

            {/* Upcoming Competitions */}
            {competitions.filter(comp => comp.status === 'upcoming').length > 0 && (
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">
                    Compétitions à venir
                  </h2>
                </div>
                <div className="p-6">
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {competitions
                      .filter(comp => comp.status === 'upcoming')
                      .map(competition => {
                        const teamInfo = getTeamInfo(competition)
                        
                        return (
                          <div 
                            key={competition.id}
                            className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                          >
                            <div 
                              className="p-5 border-b border-gray-200 bg-gradient-to-r from-gray-500 to-gray-600"
                              style={teamInfo ? {
                                background: `linear-gradient(to right, ${teamInfo.team_color}, ${adjustColor(teamInfo.team_color, -20)})`
                              } : {}}
                            >
                              <div className="flex justify-between items-start">
                                <Trophy className="h-8 w-8 text-white" />
                                {getStatusBadge(competition.status)}
                              </div>
                              <h3 className="mt-2 text-xl font-bold text-white">{competition.title}</h3>
                              <div className="mt-2 flex items-center text-white/80">
                                <Calendar className="h-4 w-4 mr-1" />
                                <span className="text-sm">
                                  {new Date(competition.start_date).toLocaleDateString()} - {new Date(competition.end_date).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            
                            <div className="p-5">
                              {competition.description && (
                                <p className="text-sm text-gray-600 mb-4">{competition.description}</p>
                              )}
                              
                              {teamInfo && (
                                <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: `${teamInfo.team_color}20` }}>
                                  <div className="flex items-center">
                                    <div 
                                      className="h-8 w-8 rounded-full flex items-center justify-center text-white"
                                      style={{ backgroundColor: teamInfo.team_color }}
                                    >
                                      <Users className="h-4 w-4" />
                                    </div>
                                    <div className="ml-2">
                                      <h4 className="text-sm font-medium">{teamInfo.team_name}</h4>
                                      <div className="flex items-center text-xs text-gray-500">
                                        <Users className="h-3 w-3 mr-1" />
                                        {teamInfo.team_members} membres
                                      </div>
                                    </div>
                                  </div>
                                  <div className="mt-3">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="w-full"
                                      onClick={() => {
                                        setSelectedCompetition(competition)
                                        fetchTeamMembers(competition.my_manager!.id)
                                        setShowTeamModal(true)
                                      }}
                                    >
                                      Voir l'équipe
                                    </Button>
                                  </div>
                                </div>
                              )}
                              
                              {competition.prize && (
                                <div className="flex items-center mb-4 text-amber-600">
                                  <Award className="h-5 w-5 mr-2" />
                                  <span className="text-sm font-medium">{competition.prize}</span>
                                </div>
                              )}
                              
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Participants</h4>
                              <div className="space-y-2 mb-4">
                                {competition.participants.map((participant) => (
                                  <div 
                                    key={participant.manager_id}
                                    className={`flex items-center p-2 rounded-lg ${
                                      participant.manager_id === competition.my_manager?.id ? 'border-2' : ''
                                    }`}
                                    style={{ 
                                      backgroundColor: `${participant.team_color}20`,
                                      borderColor: participant.manager_id === competition.my_manager?.id ? participant.team_color : 'transparent'
                                    }}
                                  >
                                    <div 
                                      className="h-4 w-4 rounded-full mr-2"
                                      style={{ backgroundColor: participant.team_color }}
                                    ></div>
                                    <span className="text-sm">{participant.team_name}</span>
                                    <span className="text-xs text-gray-500 ml-2">
                                      ({participant.team_members} livreurs)
                                    </span>
                                  </div>
                                ))}
                              </div>
                              
                              <div className="text-xs text-gray-500">
                                Commence le {new Date(competition.start_date).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                  </div>
                </div>
              </div>
            )}

            {/* Completed Competitions */}
            {competitions.filter(comp => comp.status === 'completed').length > 0 && (
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">
                    Compétitions terminées
                  </h2>
                </div>
                <div className="p-6">
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {competitions
                      .filter(comp => comp.status === 'completed')
                      .map(competition => {
                        const teamInfo = getTeamInfo(competition)
                        const teamRank = getTeamRank(competition)
                        const position = teamRank ? getTeamPosition(teamRank) : null
                        const winner = competition.participants.find(p => p.manager_id === competition.winner_id)
                        const isWinner = competition.my_manager?.id === competition.winner_id
                        
                        return (
                          <div 
                            key={competition.id}
                            className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                          >
                            <div 
                              className="p-5 border-b border-gray-200 bg-gradient-to-r from-amber-500 to-amber-600"
                              style={teamInfo ? {
                                background: `linear-gradient(to right, ${teamInfo.team_color}, ${adjustColor(teamInfo.team_color, -20)})`
                              } : {}}
                            >
                              <div className="flex justify-between items-start">
                                <Trophy className="h-8 w-8 text-white" />
                                {getStatusBadge(competition.status)}
                              </div>
                              <h3 className="mt-2 text-xl font-bold text-white">{competition.title}</h3>
                              <div className="mt-2 flex items-center text-white/80">
                                <Calendar className="h-4 w-4 mr-1" />
                                <span className="text-sm">
                                  {new Date(competition.start_date).toLocaleDateString()} - {new Date(competition.end_date).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            
                            <div className="p-5">
                              {isWinner && (
                                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                  <div className="flex items-center">
                                    <Crown className="h-6 w-6 text-amber-500 mr-2" />
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-900">Félicitations !</h4>
                                      <p className="text-sm text-amber-700">Votre équipe a remporté cette compétition !</p>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {teamInfo && !isWinner && (
                                <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: `${teamInfo.team_color}20` }}>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                      <div 
                                        className="h-8 w-8 rounded-full flex items-center justify-center text-white"
                                        style={{ backgroundColor: teamInfo.team_color }}
                                      >
                                        <Users className="h-4 w-4" />
                                      </div>
                                      <div className="ml-2">
                                        <h4 className="text-sm font-medium">{teamInfo.team_name}</h4>
                                        <div className="flex items-center text-xs text-gray-500">
                                          <Package className="h-3 w-3 mr-1" />
                                          {teamInfo.completed_missions} missions
                                        </div>
                                      </div>
                                    </div>
                                    {position && position.icon && (
                                      <div className="flex flex-col items-center">
                                        {position.icon}
                                        <span className={`text-xs font-bold ${position.color}`}>{position.text}</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="mt-2 text-sm font-bold">{teamInfo.score} points</div>
                                </div>
                              )}
                              
                              {winner && !isWinner && (
                                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                  <div className="flex items-center">
                                    <Crown className="h-6 w-6 text-amber-500 mr-2" />
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-900">Gagnant</h4>
                                      <p className="text-sm text-amber-700">{winner.team_name}</p>
                                    </div>
                                  </div>
                                  <div className="mt-2 flex items-center text-amber-700">
                                    <Star className="h-4 w-4 mr-1" />
                                    <span className="text-sm">{winner.score} points</span>
                                  </div>
                                </div>
                              )}
                              
                              {competition.prize && (
                                <div className="flex items-center mb-4 text-amber-600">
                                  <Award className="h-5 w-5 mr-2" />
                                  <span className="text-sm font-medium">{competition.prize}</span>
                                </div>
                              )}
                              
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Résultats finaux</h4>
                              <div className="space-y-2 mb-4">
                                {[...competition.participants]
                                  .sort((a, b) => b.score - a.score)
                                  .slice(0, 5) // Show top 5
                                  .map((participant, index) => (
                                    <div 
                                      key={participant.manager_id}
                                      className={`flex items-center justify-between p-2 rounded-lg ${
                                        participant.manager_id === competition.my_manager?.id ? 'border-2' : ''
                                      }`}
                                      style={{ 
                                        backgroundColor: `${participant.team_color}20`,
                                        borderColor: participant.manager_id === competition.my_manager?.id ? participant.team_color : 'transparent'
                                      }}
                                    >
                                      <div className="flex items-center">
                                        <div 
                                          className="h-6 w-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                          style={{ backgroundColor: participant.team_color }}
                                        >
                                          {index + 1}
                                        </div>
                                        <span className="ml-2 text-sm font-medium">{participant.team_name}</span>
                                      </div>
                                      <div className="text-sm font-bold">{participant.score} pts</div>
                                    </div>
                                  ))
                                }
                              </div>
                              
                              <div className="text-xs text-gray-500">
                                Terminée le {new Date(competition.end_date).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// Helper function to adjust color brightness
function adjustColor(color: string, amount: number): string {
  // Remove # if present
  color = color.replace(/^#/, '');
  
  // Parse the color
  let r = parseInt(color.substring(0, 2), 16);
  let g = parseInt(color.substring(2, 4), 16);
  let b = parseInt(color.substring(4, 6), 16);
  
  // Adjust each component
  r = Math.max(0, Math.min(255, r + amount));
  g = Math.max(0, Math.min(255, g + amount));
  b = Math.max(0, Math.min(255, b + amount));
  
  // Convert back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// Missing MapPin component
function MapPin(props: any) {
  return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
}